import React, { useRef, useState, useCallback, useEffect } from 'react';
import Tesseract from 'tesseract.js';
import { PhotoCamera, Close, RotateLeft, Check, CloudUpload, QrCodeScanner, FlashOn } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';

const OCRImageCapture = ({ onTextExtracted, autoOpenCamera, onCloseCamera }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const fileInputRef = useRef(null);
    const [isStreaming, setIsStreaming] = useState(false);
    const [capturedImage, setCapturedImage] = useState(null);
    const [facingMode, setFacingMode] = useState('environment');
    const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 0, height: 0 });
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [screenHeight, setScreenHeight] = useState(window.innerHeight);
    const [showCamera, setShowCamera] = useState(autoOpenCamera || false);
    const [previewUrls, setPreviewUrls] = useState([]);
    const [imageFile, setImageFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState('');

    const getAspectRatio = useCallback(() => {
        return screenWidth < 640 ? 5 / 3 : 3 / 2;
    }, [screenWidth]);

    const getResponsiveDimensions = useCallback(() => {
        const isSmallScreen = screenWidth < 640;
        const maxWidth = isSmallScreen ? screenWidth * 0.8 : screenWidth * 0.95;
        const maxHeight = isSmallScreen ? screenHeight * 0.7 : screenHeight * 0.85;
        const aspectRatio = screenWidth < 640 ? 3 / 5 : 3 / 2.5;

        let finalWidth = maxWidth;
        let finalHeight = finalWidth / aspectRatio;

        if (finalHeight > maxHeight) {
            finalHeight = maxHeight;
            finalWidth = finalHeight * aspectRatio;
        }

        return { width: finalWidth, height: finalHeight };
    }, [screenWidth, screenHeight]);

    const [dimensions, setDimensions] = useState(getResponsiveDimensions());

    const startCamera = useCallback(async () => {
        try {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
                streamRef.current = null;
            }

            const constraints = {
                video: {
                    facingMode: facingMode,
                    width: { ideal: 1920 },
                    height: { ideal: 1080 },
                },
            };

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.onloadedmetadata = () => {
                    videoRef.current
                        .play()
                        .then(() => setIsStreaming(true))
                        .catch((err) => {
                            console.error('Error playing video:', err);
                            alert('Failed to start camera stream.');
                        });
                };
            }
        } catch (error) {
            console.error('Error accessing camera:', error);
            alert('Unable to access camera. Please check permissions.');
        }
    }, [facingMode]);

    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setIsStreaming(false);
    }, []);

    const calculateCropArea = useCallback(() => {
        if (videoRef.current) {
            const video = videoRef.current;
            const videoRect = video.getBoundingClientRect();

            const maxFrameWidth = screenWidth < 640 ? 280 : 360;
            const frameWidth = Math.min(videoRect.width * 0.8, maxFrameWidth);
            const aspectRatio = getAspectRatio();
            const frameHeight = frameWidth / aspectRatio;

            const frameX = (videoRect.width - frameWidth) / 2;
            const frameY = (videoRect.height - frameHeight) / 2;

            const scaleX = video.videoWidth / videoRect.width;
            const scaleY = video.videoHeight / videoRect.height;

            return {
                x: frameX * scaleX,
                y: frameY * scaleY,
                width: frameWidth * scaleX,
                height: frameHeight * scaleY,
            };
        }
        return { x: 0, y: 0, width: 0, height: 0 };
    }, [screenWidth, getAspectRatio]);

    const captureImage = useCallback(() => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');

            const aspectRatio = getAspectRatio();
            const cropWidth = video.videoWidth * 0.6;
            const cropHeight = cropWidth / aspectRatio;

            const cropX = (video.videoWidth - cropWidth) / 2;
            const cropY = (video.videoHeight - cropHeight) / 2;

            canvas.width = cropWidth;
            canvas.height = cropHeight;

            if (context) {
                context.drawImage(
                    video,
                    cropX,
                    cropY,
                    cropWidth,
                    cropHeight,
                    0,
                    0,
                    cropWidth,
                    cropHeight
                );

                const imageDataUrl = canvas.toDataURL('image/jpeg', 0.9);
                setCapturedImage(imageDataUrl);
                setCropArea({ x: cropX, y: cropY, width: cropWidth, height: cropHeight });
                stopCamera();
            }
        }
    }, [stopCamera, getAspectRatio]);

    const confirmCapture = useCallback(() => {
        if (capturedImage && canvasRef.current) {
            canvasRef.current.toBlob((blob) => {
                if (blob) {
                    const file = new File([blob], 'captured-business-card.jpg', {
                        type: 'image/jpeg',
                    });
                    setImageFile(file);
                    setPreviewUrls([URL.createObjectURL(file)]);
                    setShowCamera(false);
                    setCapturedImage(null);
                    if (onCloseCamera) onCloseCamera();
                }
            }, 'image/jpeg', 0.9);
        }
    }, [capturedImage, onCloseCamera]);

    const retakePhoto = useCallback(() => {
        setCapturedImage(null);
        setImageFile(null);
        setPreviewUrls([]);
        startCamera();
    }, [startCamera]);

    const switchCamera = useCallback(() => {
        setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'));
        stopCamera();
        setTimeout(startCamera, 300);
    }, [startCamera, stopCamera]);

    const handleFileSelect = (files) => {
        const imageFiles = Array.from(files).filter((file) => file.type.startsWith('image/'));
        const latestFiles = imageFiles.slice(-1);

        const newUrls = latestFiles.map((file) => {
            setImageFile(file);
            return URL.createObjectURL(file);
        });

        setPreviewUrls(newUrls);
        setShowCamera(false);
        if (onCloseCamera) onCloseCamera();
    };

    const handleFileInputChange = (e) => {
        if (e.target.files) handleFileSelect(e.target.files);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        if (e.dataTransfer.files) handleFileSelect(e.dataTransfer.files);
    };

    const handleRemoveImage = (index) => {
        setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
        setImageFile(null);
        setStatus('');
        setProgress(0);
    };

    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
            setScreenHeight(window.innerHeight);
            setDimensions(getResponsiveDimensions());
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [getResponsiveDimensions]);

    useEffect(() => {
        if (showCamera) {
            startCamera();
        } else {
            stopCamera();
        }
        return () => stopCamera();
    }, [showCamera, startCamera, stopCamera, facingMode]);

    useEffect(() => {
        setShowCamera(autoOpenCamera || false);
    }, [autoOpenCamera]);

    useEffect(() => {
        if (imageFile) {
            const timer = setTimeout(() => {
                processImage();
            }, 1000);
            return () => clearTimeout(timer); // Cleanup to prevent memory leaks
        }
    }, [imageFile]);

    const processImage = async () => {
        setIsProcessing(true);
        setStatus('Running OCR…');

        try {
            const {
                data: { text }
            } = await Tesseract.recognize(imageFile, 'eng', {
                logger: (m) => {
                    if (m.status === 'recognizing text') {
                        setProgress(Math.round(m.progress * 100));
                    }
                }
            });

            onTextExtracted(text);
            setStatus('Text extraction completed!');
        } catch (error) {
            console.error('OCR Error:', error);
            setStatus('Error processing image. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="relative flex items-center justify-center">
            {!showCamera && previewUrls.length === 0 ? (
                <div className="bg-white rounded-xl shadow-lg p-5 border border-gray-100">
                    <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                        <QrCodeScanner className="mr-3 h-7 w-7 text-blue-600" />
                        Scan Business Card
                    </h2>
                    <div className="flex justify-center items-center">
                        <button
                            onClick={() => setShowCamera(true)}
                            className="flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full hover:bg-blue-700 transition-colors shadow-lg"
                        >
                            <PhotoCamera className="h-8 w-8 text-white" />
                        </button>
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-xl w-6xl shadow-lg p-2 border border-gray-100">
                    {showCamera ? (
                        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[1000] p-4">
                            <div
                                className="bg-black rounded-lg overflow-hidden shadow-2xl flex flex-col"
                                style={{
                                    width: dimensions.width,
                                    height: dimensions.height,
                                    maxWidth: '95vw',
                                    maxHeight: '80vh',
                                }}
                            >
                                <div className="flex items-center justify-between p-3 bg-gray-900 text-white">
                                    <h2 className="text-sm font-semibold flex items-center">
                                        <QrCodeScanner className="h-4 w-4 mr-2" />
                                        Scan Business Card
                                    </h2>
                                    <button
                                        onClick={() => {
                                            setShowCamera(false);
                                            if (onCloseCamera) onCloseCamera();
                                        }}
                                        className="p-2 rounded-full hover:bg-white/20 transition-colors"
                                    >
                                        <Close className="h-5 w-5" />
                                    </button>
                                    {isStreaming && (
                                        <button
                                            onClick={switchCamera}
                                            className="p-2 rounded-full hover:bg-white/20 transition-colors"
                                        >
                                            <RotateLeft className="h-5 w-5" />
                                        </button>
                                    )}
                                </div>
                                <div className="flex-1 relative overflow-hidden">
                                    {capturedImage ? (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-900">
                                            <img
                                                src={capturedImage}
                                                alt="Captured business card"
                                                className="max-w-full max-h-full object-contain rounded"
                                            />
                                        </div>
                                    ) : (
                                        <>
                                            <video
                                                ref={videoRef}
                                                className="w-full h-full object-cover"
                                                playsInline
                                                muted
                                            />
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <div className="absolute inset-0 bg-black/40"></div>
                                                <div className="relative z-10">
                                                    <div
                                                        className="border-2 border-blue-400 rounded-lg relative bg-transparent"
                                                        style={{
                                                            width: Math.min(dimensions.width * 0.8, screenWidth < 640 ? 280 : 360),
                                                            height: Math.min(dimensions.width * 0.8, screenWidth < 640 ? 280 : 360) / getAspectRatio(),
                                                        }}
                                                    >
                                                        <div className="absolute inset-0 border-2 border-dashed border-blue-300/50 rounded-lg"></div>
                                                        <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 text-white text-xs bg-black/70 px-3 py-1 rounded-full whitespace-nowrap">
                                                            Position card within frame
                                                        </div>
                                                        <div className="absolute -top-1 -left-1 w-4 h-4 border-l-3 border-t-3 border-blue-400"></div>
                                                        <div className="absolute -top-1 -right-1 w-4 h-4 border-r-3 border-t-3 border-blue-400"></div>
                                                        <div className="absolute -bottom-1 -left-1 w-4 h-4 border-l-3 border-b-3 border-blue-400"></div>
                                                        <div className="absolute -bottom-1 -right-1 w-4 h-4 border-r-3 border-b-3 border-blue-400"></div>
                                                    </div>
                                                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                                <div className="p-4 bg-gray-900">
                                    {capturedImage ? (
                                        <div className="flex items-center justify-center space-x-4">
                                            <button
                                                onClick={() => fileInputRef.current?.click()}
                                                className="flex items-center justify-center w-12 h-12 bg-gray-600 rounded-full hover:bg-gray-500 transition-colors"
                                            >
                                                <CloudUpload className="h-6 w-6 text-white" />
                                            </button>
                                            <button
                                                onClick={retakePhoto}
                                                className="flex items-center justify-center w-12 h-12 bg-gray-600 rounded-full hover:bg-gray-500 transition-colors"
                                            >
                                                <RotateLeft className="h-6 w-6 text-white" />
                                            </button>
                                            <button
                                                onClick={confirmCapture}
                                                className="flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full hover:bg-blue-500 transition-colors"
                                            >
                                                <Check className="h-8 w-8 text-white" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex flex-row">
                                            <div className="relative w-full h-24">
                                                <button
                                                    onClick={() => fileInputRef.current?.click()}
                                                    className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-gray-600 rounded-full hover:bg-gray-500 transition-colors flex items-center justify-center"
                                                    title="Upload"
                                                >
                                                    <CloudUpload className="h-6 w-6 text-white" />
                                                </button>
                                                <button
                                                    onClick={captureImage}
                                                    disabled={!isStreaming}
                                                    className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-white rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center"
                                                    title="Capture"
                                                >
                                                    <div className="w-14 h-14 bg-white border-4 border-gray-300 rounded-full flex items-center justify-center">
                                                        <PhotoCamera className="h-6 w-6 text-gray-600" />
                                                    </div>
                                                </button>
                                                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileInputChange} className="hidden" />
                                            </div>
                                        </div>
                                    )}
                                    <div className="text-center mt-2">
                                        <p className="text-xs text-gray-400">
                                            {capturedImage ? 'Review your capture' : 'Only the framed area will be captured'}
                                        </p>
                                    </div>
                                </div>
                                <canvas ref={canvasRef} className="hidden" />
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className={previewUrls.length === 1 ? 'flex justify-center' : 'grid grid-cols-1 sm:grid-cols-2 gap-4'}>
                                {previewUrls.map((url, index) => (
                                    <div key={index} className="relative group">
                                        <img
                                            src={url}
                                            alt={`Preview ${index + 1}`}
                                            className="max-w-full max-h-48 object-contain border-2 border-gray-200 rounded-xl shadow-md group-hover:shadow-lg transition-shadow mx-auto"
                                        />
                                        <button
                                            onClick={() => handleRemoveImage(index)}
                                            className="absolute top-3 right-3 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors shadow-lg opacity-0 group-hover:opacity-100"
                                        >
                                            <Close className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            {imageFile && (
                                <div className="bg-white rounded-lg shadow-md p-5 mt-4">
                                    {isProcessing ? (
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-center">
                                                <CircularProgress className="h-8 w-8 animate-spin text-blue-600" />
                                            </div>
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm font-medium text-gray-700">Progress</span>
                                                    <span className="text-sm text-gray-600">{progress}%</span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-2">
                                                    <div
                                                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                                        style={{ width: `${progress}%` }}
                                                    />
                                                </div>
                                                <p className="text-sm text-gray-600 text-center">{status}</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center">
                                            <p className="text-sm text-gray-600">{status}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileInputChange}
                className="hidden"
            />
        </div>
    );
};

export default OCRImageCapture;